import { StandardEvents } from '@shopify/events';

/* Mara Linden cart drawer. Optimistic: every change shows instantly, the server syncs in the background. */

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

function money(cents, format) {
  const f = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
  const m = f.match(/\{\{\s*(\w+)\s*\}\}/);
  const type = m ? m[1] : 'amount';
  const num = (dec, th, dp) => {
    const s = (Math.round(cents || 0) / 100).toFixed(dec);
    let [i, d] = s.split('.');
    i = i.replace(/\B(?=(\d{3})+(?!\d))/g, th);
    return d ? i + dp + d : i;
  };
  let v;
  switch (type) {
    case 'amount_no_decimals': v = num(0, ',', '.'); break;
    case 'amount_with_comma_separator': v = num(2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': v = num(0, '.', ','); break;
    case 'amount_with_apostrophe_separator': v = num(2, "'", '.'); break;
    default: v = num(2, ',', '.');
  }
  return f.replace(/\{\{\s*\w+\s*\}\}/, v);
}

const store = {
  get(k) { try { return localStorage.getItem(k); } catch (_) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (_) {} },
  sget(k) { try { return sessionStorage.getItem(k); } catch (_) { return null; } },
  sset(k, v) { try { sessionStorage.setItem(k, v); } catch (_) {} },
};

const imgSize = (url, w) => {
  if (!url) return '';
  const u = url.startsWith('//') ? 'https:' + url : url;
  return u + (u.includes('?') ? '&' : '?') + 'width=' + w;
};

class MlCart extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.cfg = JSON.parse(this.querySelector('[data-ml-config]').textContent);
    this.cart = this.#readJson(this.querySelector('[data-ml-cart-json]').textContent) || { items: [] };
    this.drawer = this.closest('theme-drawer');

    this.el = {
      title: this.querySelector('[data-ml-title]'),
      timer: this.querySelector('[data-ml-timer]'),
      rewards: this.querySelector('[data-ml-rewards]'),
      error: this.querySelector('[data-ml-error]'),
      lines: this.querySelector('[data-ml-lines]'),
      empty: this.querySelector('[data-ml-empty]'),
      upsells: this.querySelector('[data-ml-upsells]'),
      footer: this.querySelector('[data-ml-footer]'),
      protection: this.querySelector('[data-ml-protection]'),
      checkout: this.querySelector('[data-ml-checkout]'),
    };

    this.pending = 0;
    this.queue = Promise.resolve();
    this.debounce = new Map();
    this.recCache = new Map();
    this.base = this.cfg.root.replace(/\/$/, '') + '/';
    this.upsellSig = '';
    this.tmp = 0;
    this.holdProt = false;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onExternalUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    document.addEventListener('shopify:section:select', this.#onEditorSelect);

    this.#resetCheckoutButton();
    this.#recalc();
    this.render();
    this.#ensureProtection();
    this.#syncBubble();
    this.#startTimer();

    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 300));
    idle(() => this.#loadUpsells());
  }

  disconnectedCallback() {
    this._ready = false;
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onExternalUpdate);
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    window.removeEventListener('pageshow', this.#onPageShow);
    clearInterval(this._timerI);
  }

  // Coming back to the page (browser back button) restores it from memory with the checkout button still in
  // its "loading" state. Always reset it, so customers can check out every time, timer or no timer.
  #resetCheckoutButton() {
    clearTimeout(this._checkoutT);
    this.el.checkout?.classList.remove('is-loading');
  }

  #onPageShow = (e) => {
    this.#resetCheckoutButton();
    if (e.persisted) this.#enqueue(() => this.#fetchCart());
  };

  // Theme editor: selecting the section opens the drawer so changes are visible
  #onEditorSelect = (e) => { if (e.target instanceof Element && e.target.contains(this)) this.drawer?.open?.(); };

  /* ---------- data ---------- */

  #readJson(text) {
    try { return JSON.parse(text); } catch (_) { return null; }
  }

  #parseSection(html) {
    if (!html) return null;
    const m = html.match(/<script[^>]*data-ml-cart-json[^>]*>([\s\S]*?)<\/script>/);
    return m ? this.#readJson(m[1]) : null;
  }

  get realItems() { return this.cart.items.filter((i) => !i.protection); }
  get protItem() { return this.cart.items.find((i) => i.protection); }

  #recalc() {
    const items = this.cart.items;
    items.forEach((i) => { i.line_price = i.price * i.quantity; });
    const subtotal = items.reduce((s, i) => s + i.line_price, 0);
    if (this.cart.cartDiscount === undefined) {
      this.cart.cartDiscount = Math.max(0, (this.cart.items_subtotal_price || 0) - (this.cart.total_price || 0));
    }
    this.cart.items_subtotal_price = subtotal;
    this.cart.total_price = Math.max(0, subtotal - (items.length ? this.cart.cartDiscount : 0));
    this.cart.item_count = items.reduce((s, i) => s + i.quantity, 0);
  }

  #applyServer(cart) {
    if (!cart) return;
    // While the theme's own add request is still running, the server cart is not final yet
    if (this.holdProt && !cart.items.some((i) => !i.protection)) return;
    this.cart = cart;
    this.#recalc();
    this.#ensureProtection();
    this.render();
    this.#syncBubble();
    this.#loadUpsells();
  }

  #enqueue(fn) {
    this.pending++;
    this.queue = this.queue.then(async () => {
      let result = null;
      try { result = await fn(); } catch (err) { this.#showError(err?.message); }
      this.pending--;
      if (this.pending === 0) {
        if (result?.cart) this.#applyServer(result.cart);
        else { try { const r = await this.#fetchCart(); this.#applyServer(r.cart); } catch (_) {} }
      }
    });
    return this.queue;
  }

  async #post(path, body) {
    const res = await fetch(this.base + path.replace(/^\//, ''), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'ml-cart-data' }),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'Cart error');
    return { cart: this.#parseSection(json.sections?.['ml-cart-data']) };
  }

  async #fetchCart() {
    const res = await fetch(this.base + '?sections=ml-cart-data', { headers: { Accept: 'application/json' } });
    const json = await res.json();
    return { cart: this.#parseSection(json['ml-cart-data']) };
  }

  /* ---------- actions ---------- */

  #setQty(key, qty) {
    const item = this.cart.items.find((i) => i.key === key);
    if (!item) return;
    qty = Math.max(0, qty);
    if (qty === 0) this.cart.items = this.cart.items.filter((i) => i !== item);
    else item.quantity = qty;
    this.#recalc();
    this.#ensureProtection();
    this.render();
    this.#syncBubble();

    // tmp rows: the add is still queued, so change by variant id once it lands
    const id = key.startsWith('tmp-') ? String(item.variant_id) : key;
    if (this.debounce.has(key)) clearTimeout(this.debounce.get(key));
    else this.pending++; // reserve so an older response can't overwrite this change
    this.debounce.set(key, setTimeout(() => {
      this.debounce.delete(key);
      this.pending--;
      this.#enqueue(() => this.#post('/cart/change.js', { id, quantity: qty }));
    }, qty === 0 ? 0 : 250));
  }

  #addVariant(v, product) {
    const existing = this.cart.items.find((i) => i.variant_id === v.id && !i.protection);
    if (existing) {
      existing.quantity += 1;
    } else {
      this.cart.items.unshift({
        key: 'tmp-' + ++this.tmp,
        variant_id: v.id,
        product_id: product.id,
        title: product.title,
        variant_title: product.variants.length > 1 ? v.title : null,
        url: product.url,
        image: product.image,
        quantity: 1,
        price: v.price,
        original_price: v.price,
        compare_at: v.compare || 0,
        protection: false,
      });
    }
    this.#timerOnAdd();
    this.#recalc();
    // Product first, protection after it, so both always land in the same cart
    this.render();
    this.#syncBubble();
    this.#enqueue(() => this.#post('/cart/add.js', { items: [{ id: v.id, quantity: 1 }] }));
  }

  get #protectionWanted() {
    const v = store.get('ml_protection');
    if (v === 'on') return true;
    if (v === 'off') return false;
    return !!this.cfg.protection.default;
  }

  #ensureProtection() {
    const p = this.cfg.protection;
    if (!p.show || !p.variantId || this.protFailed || this.holdProt) return;
    const prot = this.protItem;
    const hasReal = this.realItems.some((i) => !String(i.key).startsWith('tmp-'));
    const hasAny = this.realItems.length > 0;
    const want = hasAny && this.#protectionWanted;

    if (want && !prot) {
      // Only add protection once a real product line exists on the server
      if (!hasReal) return;
      this.cart.items.push({
        key: 'tmp-prot', variant_id: p.variantId, product_id: p.productId, title: p.title,
        quantity: 1, price: p.price, original_price: p.price, compare_at: 0, protection: true,
      });
      this.#recalc();
      this.#enqueue(() =>
        this.#post('/cart/add.js', { items: [{ id: p.variantId, quantity: 1 }] }).catch(() => {
          this.protFailed = true; // never retry in a loop if the product can't be added
          return null;
        })
      );
    } else if (prot && (!want || prot.quantity !== 1)) {
      const q = want ? 1 : 0;
      if (q === 0) this.cart.items = this.cart.items.filter((i) => i !== prot);
      else prot.quantity = 1;
      this.#recalc();
      if (!prot.key.startsWith('tmp-')) this.#enqueue(() => this.#post('/cart/change.js', { id: prot.key, quantity: q }));
    }
  }

  #onClick = (e) => {
    // Clicks inside the cart never reach the drawer's outside-click check.
    // Re-rendering removes the clicked button from the page, which the drawer would otherwise read as a click outside and close.
    e.stopPropagation();
    const t = e.target.closest('[data-action]');
    if (!t) return;
    const a = t.dataset.action;
    const key = t.closest('[data-key]')?.dataset.key;

    if (a === 'close') { e.preventDefault(); this.drawer?.close?.(); }
    else if (a === 'plus' || a === 'minus') {
      const item = this.cart.items.find((i) => i.key === key);
      if (item) this.#setQty(key, item.quantity + (a === 'plus' ? 1 : -1));
    } else if (a === 'remove') { this.#setQty(key, 0); }
    else if (a === 'protection') {
      store.set('ml_protection', this.protItem ? 'off' : 'on');
      this.#ensureProtection();
      this.render();
    } else if (a === 'upsell-add') {
      const card = t.closest('[data-pid]');
      const product = this.upsellProducts?.find((p) => String(p.id) === card.dataset.pid);
      if (!product) return;
      const sel = card.querySelector('select');
      const v = product.variants.find((x) => String(x.id) === (sel ? sel.value : String(product.variants.find((y) => y.available)?.id)));
      if (v) this.#addVariant(v, product);
    } else if (a === 'prev' || a === 'next') {
      const track = this.querySelector('[data-ml-track]');
      if (track) track.scrollBy({ left: (a === 'next' ? 1 : -1) * track.clientWidth, behavior: 'smooth' });
    } else if (a === 'checkout') {
      e.preventDefault();
      t.classList.add('is-loading');
      // Never leave the button stuck: if the redirect has not happened within a few seconds, make it clickable again
      clearTimeout(this._checkoutT);
      this._checkoutT = setTimeout(() => this.#resetCheckoutButton(), 4000);
      const go = () => { window.location.href = this.cfg.checkoutUrl; };
      // Wait for pending cart updates, but no longer than 2.5 seconds
      Promise.race([this.queue, new Promise((r) => setTimeout(r, 2500))]).then(go, go);
    }
  };

  #onChange = (e) => {
    const input = e.target.closest('[data-action="qty-input"]');
    if (!input) return;
    const key = input.closest('[data-key]')?.dataset.key;
    const v = parseInt(input.value, 10);
    if (key && !Number.isNaN(v)) this.#setQty(key, v);
  };

  #onExternalUpdate = (event) => {
    const isAdd = event.action === 'add';
    if (isAdd) {
      this.#timerOnAdd();
      // Hold shipping protection until the theme's add has finished. Two adds at the same time on a
      // brand new visitor create two separate carts and the product can get lost.
      this.holdProt = true;
      clearTimeout(this._holdT);
      this._holdT = setTimeout(() => { this.holdProt = false; }, 10000);
    }
    const release = () => { if (isAdd) { this.holdProt = false; clearTimeout(this._holdT); } };

    if (isAdd && this.cfg.autoOpen && !this.drawer?.isOpen) {
      const modal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
      if (!modal) this.drawer?.open?.();
      else modal.addEventListener('close', () => this.drawer?.open?.(), { once: true });
    }
    if (isAdd && this.pending === 0) this.#optimisticAdd(event);
    this.classList.add('is-syncing');
    const done = () => this.classList.remove('is-syncing');
    if (!event.promise) { release(); this.#enqueue(() => this.#fetchCart()).then(done); return; }
    event.promise
      .then(({ detail } = {}) => {
        release();
        // Fast path: show the theme's fresh cart immediately, then confirm in the background
        if (Array.isArray(detail?.items) && this.pending === 0) this.#applyAjaxItems(detail.items);
        done();
        return this.#enqueue(() => this.#fetchCart());
      })
      .catch(() => { release(); done(); return this.#enqueue(() => this.#fetchCart()); });
  };

  // Show the product in the drawer the moment "Add to cart" is clicked, using the variant data already on the page
  #optimisticAdd(event) {
    try {
      const line = event.lines?.[0];
      const id = Number(String(line?.merchandiseId ?? '').split('/').pop());
      if (!id) return;
      const qty = Number(line.quantity) || 1;
      const existing = this.cart.items.find((i) => i.variant_id === id && !i.protection);
      if (existing) {
        existing.quantity += qty;
      } else {
        let v = null;
        for (const s of document.querySelectorAll('script[type="application/json"]')) {
          if (!s.textContent.includes(`"id":${id},`)) continue;
          const d = this.#readJson(s.textContent);
          if (d && d.id === id && 'price' in d) { v = d; break; }
        }
        if (!v) return;
        const scope = event.target instanceof Element ? event.target.closest('.shopify-section, dialog, product-card') : null;
        const title = scope?.querySelector('h1, .product-title, [class*="title"] a')?.textContent.trim() || String(v.name || '').split(' - ')[0];
        const media = scope?.querySelector('add-to-cart-component')?.dataset.productVariantMedia || v.featured_image?.src || '';
        this.cart.items.unshift({
          key: 'tmp-' + ++this.tmp,
          variant_id: id,
          product_id: Number(scope?.querySelector('[data-product-id]')?.dataset.productId) || null,
          title,
          variant_title: v.title && v.title !== 'Default Title' ? v.title : null,
          url: location.pathname,
          image: media ? imgSize(media.replace(/[?&]width=\d+/, ''), 192) : '',
          quantity: qty,
          price: v.price,
          original_price: v.price,
          compare_at: v.compare_at_price || 0,
          protection: false,
        });
      }
      this.#recalc();
      this.render();
      this.#syncBubble();
    } catch (_) { /* fall back to the server response */ }
  }

  #applyAjaxItems(items) {
    const known = new Map(this.cart.items.map((i) => [i.variant_id, i]));
    const pv = this.cfg.protection.variantId;
    this.cart = {
      ...this.cart,
      items: items.map((a) => {
        const k = known.get(a.variant_id);
        return {
          key: a.key, variant_id: a.variant_id, product_id: a.product_id,
          title: a.product_title, variant_title: a.variant_title, url: a.url,
          image: imgSize(a.featured_image?.url || a.image, 192),
          quantity: a.quantity, price: a.final_price, original_price: a.original_price,
          compare_at: k?.compare_at || 0, protection: a.variant_id === pv,
        };
      }),
    };
    this.#recalc();
    this.#ensureProtection();
    this.render();
    this.#syncBubble();
  }

  #showError(msg) {
    if (!this.el.error) return;
    this.el.error.textContent = msg || '';
    this.el.error.hidden = !msg;
    clearTimeout(this._errT);
    if (msg) this._errT = setTimeout(() => { this.el.error.hidden = true; }, 6000);
  }

  #syncBubble() {
    const count = this.realItems.reduce((s, i) => s + i.quantity, 0);
    customElements.whenDefined('cart-icon').then(() => {
      document.querySelectorAll('cart-icon').forEach((icon) => icon.renderCartBubble?.(count, false));
    });
  }

  /* ---------- timer ---------- */
  // Only a reminder. It never blocks checkout. Starts when the first product lands in the cart,
  // keeps running across pages, resets when the cart is emptied, and starts over when something is added after it ran out.

  #timerOnAdd() {
    const end = parseInt(store.sget('ml_timer_end') || '0', 10);
    if (!end || end <= Date.now()) store.sset('ml_timer_end', '');
    this._tick?.();
  }

  #startTimer() {
    const t = this.cfg.timer;
    if (!t.show || !this.el.timer) return;
    const minutes = Number(t.minutes) || 10;
    const tick = () => {
      if (!this.realItems.length) {
        this.el.timer.hidden = true;
        store.sset('ml_timer_end', '');
        return;
      }
      let end = parseInt(store.sget('ml_timer_end') || '0', 10);
      if (!end) { end = Date.now() + minutes * 60000; store.sset('ml_timer_end', String(end)); }
      const left = Math.max(0, end - Date.now());
      this.el.timer.hidden = false;
      if (left === 0) { this.el.timer.innerHTML = esc(t.expired); return; }
      const mm = String(Math.floor(left / 60000)).padStart(2, '0');
      const ss = String(Math.floor((left % 60000) / 1000)).padStart(2, '0');
      this.el.timer.innerHTML = esc(t.text).replace('[time]', `<strong>${mm}:${ss}</strong>`);
    };
    this._tick = tick;
    tick();
    clearInterval(this._timerI);
    this._timerI = setInterval(tick, 1000);
  }

  /* ---------- upsells ---------- */

  async #loadUpsells() {
    const u = this.cfg.upsells;
    if (!u.show) return;
    let list = [];
    const first = this.realItems[0];
    if (u.source === 'auto' && first) {
      const pid = first.product_id;
      if (!this.recCache.has(pid)) {
        this.recCache.set(pid, fetch(`${this.base}recommendations/products.json?product_id=${pid}&limit=${u.limit + 4}&intent=related`)
          .then((r) => r.json())
          .then((j) => (j.products || []).map((p) => ({
            id: p.id, title: p.title, url: p.url, image: imgSize(p.featured_image, 240),
            variants: p.variants.map((v) => ({ id: v.id, title: v.title, price: v.price, compare: v.compare_at_price || 0, available: v.available })),
          })))
          .catch(() => []));
      }
      list = await this.recCache.get(pid);
    }
    if (!list.length) list = u.collectionProducts || [];
    this.allUpsells = list;
    this.#renderUpsells();
  }

  #renderUpsells() {
    const box = this.el.upsells;
    if (!box) return;
    const u = this.cfg.upsells;
    const inCart = new Set(this.cart.items.map((i) => i.product_id));
    const list = (this.allUpsells || [])
      .filter((p) => !inCart.has(p.id) && p.id !== this.cfg.protection.productId && p.variants.some((v) => v.available))
      .slice(0, u.limit);
    this.upsellProducts = list;

    const sig = list.map((p) => p.id).join(',') + '|' + (this.realItems.length > 0);
    if (sig === this.upsellSig) return;
    this.upsellSig = sig;

    if (!list.length || !this.realItems.length) { box.hidden = true; box.innerHTML = ''; return; }
    box.hidden = false;
    const fmt = this.cfg.moneyFormat;
    box.innerHTML = `
      <p class="ml-upsells__title">${esc(u.title)}</p>
      <div class="ml-upsells__wrap">
        ${list.length > 1 ? `<button type="button" class="ml-upsells__arrow ml-upsells__arrow--prev" data-action="prev" aria-label="Previous">${this.cfg.icons.chevron}</button>` : ''}
        <div class="ml-upsells__track" data-ml-track>
          ${list.map((p) => {
            const avail = p.variants.filter((v) => v.available);
            const v0 = avail[0];
            return `<div class="ml-upsell" data-pid="${p.id}">
              <a href="${esc(p.url)}" class="ml-upsell__img">${p.image ? `<img src="${esc(p.image)}" alt="${esc(p.title)}" loading="lazy" width="120" height="120">` : ''}</a>
              <div class="ml-upsell__info">
                <a href="${esc(p.url)}" class="ml-upsell__name">${esc(p.title)}</a>
                <div class="ml-upsell__price">${v0.compare > v0.price ? `<s>${money(v0.compare, fmt)}</s> ` : ''}<span>${money(v0.price, fmt)}</span></div>
                <div class="ml-upsell__actions">
                  ${avail.length > 1 ? `<select class="ml-upsell__select" aria-label="Variant">${avail.map((v) => `<option value="${v.id}">${esc(v.title)}</option>`).join('')}</select>` : ''}
                  <button type="button" class="ml-upsell__add" data-action="upsell-add">${esc(u.button)}</button>
                </div>
              </div>
            </div>`;
          }).join('')}
        </div>
        ${list.length > 1 ? `<button type="button" class="ml-upsells__arrow ml-upsells__arrow--next" data-action="next" aria-label="Next">${this.cfg.icons.chevron}</button>` : ''}
      </div>`;
  }

  /* ---------- render ---------- */

  render() {
    const c = this.cfg;
    const fmt = c.moneyFormat;
    const real = this.realItems;
    const count = real.reduce((s, i) => s + i.quantity, 0);
    const empty = real.length === 0;

    this.classList.toggle('is-empty', empty);
    if (this.el.title) this.el.title.textContent = c.title.replace('[count]', count);
    this.el.empty.hidden = !empty;
    this.el.footer.hidden = empty;

    this.#renderRewards(real);
    this.#renderLines(real);
    this.#renderUpsells();
    this._tick?.();

    // Protection
    const p = c.protection;
    if (this.el.protection) {
      if (!p.show || !p.variantId || empty) this.el.protection.hidden = true;
      else {
        this.el.protection.hidden = false;
        const on = !!this.protItem || (this.holdProt && this.#protectionWanted);
        const sw = this.el.protection.querySelector('[data-action="protection"]');
        sw.setAttribute('aria-checked', String(on));
        sw.classList.toggle('is-on', on);
      }
    }

    // Checkout
    if (this.el.checkout) {
      this.el.checkout.innerHTML = esc(c.checkoutText).replace('[total]', esc(money(this.cart.total_price, fmt)));
    }
  }

  #renderRewards(real) {
    const box = this.el.rewards;
    if (!box) return;
    const r = this.cfg.rewards;
    const rate = Number(window.Shopify?.currency?.rate || 1);
    const tiers = (r.tiers || []).filter((t) => t.amount > 0).map((t) => ({ ...t, cents: Math.round(t.amount * rate) })).sort((a, b) => a.cents - b.cents);
    if (!r.show || !tiers.length || !real.length) { box.hidden = true; return; }
    box.hidden = false;
    const sub = real.reduce((s, i) => s + i.price * i.quantity, 0);
    const max = tiers[tiers.length - 1].cents;
    const next = tiers.find((t) => sub < t.cents);
    const reached = tiers.filter((t) => sub >= t.cents);
    const fmt = this.cfg.moneyFormat;
    const msg = next
      ? esc(r.text).replace('[amount]', `<strong>${esc(money(next.cents - sub, fmt))}</strong>`).replace('[reward]', `<strong>${esc(next.label)}</strong>`)
      : esc(r.done).replace('[reward]', `<strong>${esc(reached[reached.length - 1].label)}</strong>`);
    const pct = Math.min(100, (sub / max) * 100);
    box.innerHTML = `
      <p class="ml-rewards__text">${msg}</p>
      <div class="ml-rewards__bar"><span class="ml-rewards__fill" style="width:${pct}%"></span>
        ${tiers.map((t) => `<span class="ml-rewards__dot${sub >= t.cents ? ' is-done' : ''}" style="left:${(t.cents / max) * 100}%">${this.cfg.icons.tag}</span>`).join('')}
      </div>
      <div class="ml-rewards__labels">${tiers.map((t) => `<span style="left:${(t.cents / max) * 100}%">${esc(t.label)}</span>`).join('')}</div>`;
  }

  #lineInfo(i) {
    const c = this.cfg;
    const fmt = c.moneyFormat;
    const compare = Math.max(i.compare_at || 0, i.original_price || 0);
    const save = compare > i.price ? (compare - i.price) * i.quantity : 0;
    return `
      <div class="ml-line__top">
        <a href="${esc(i.url)}" class="ml-line__title">${esc(i.title)}</a>
        <button type="button" class="ml-line__remove" data-action="remove" aria-label="Remove">${c.icons.trash}</button>
      </div>
      ${i.variant_title ? `<p class="ml-line__variant">${esc(i.variant_title)}</p>` : ''}
      <p class="ml-line__price">
        ${save ? `<s>${money(compare * i.quantity, fmt)}</s>` : ''}
        <span>${money(i.price * i.quantity, fmt)}</span>
        ${save && c.showSavings ? `<span class="ml-line__save">${esc(c.savingsText).replace('[amount]', esc(money(save, fmt)))}</span>` : ''}
      </p>
      <div class="ml-qty">
        <button type="button" data-action="minus" aria-label="Decrease">&minus;</button>
        <input type="number" min="0" inputmode="numeric" value="${i.quantity}" data-action="qty-input" aria-label="Quantity">
        <button type="button" data-action="plus" aria-label="Increase">+</button>
      </div>`;
  }

  #renderLines(real) {
    const wrap = this.el.lines;
    const existing = new Map([...wrap.children].map((el) => [el.dataset.key, el]));
    const used = new Set();

    real.forEach((i) => {
      let el = existing.get(i.key);
      if (!el) {
        // reuse an optimistic row for the same variant so the image doesn't flash
        el = [...wrap.children].find((x) => !used.has(x) && x.dataset.key.startsWith('tmp-') && x.dataset.variant === String(i.variant_id));
        if (el) el.dataset.key = i.key;
      }
      if (!el) {
        el = document.createElement('div');
        el.className = 'ml-line';
        el.dataset.key = i.key;
        el.innerHTML = `<a href="${esc(i.url)}" class="ml-line__img">${i.image ? `<img src="${esc(i.image)}" alt="${esc(i.title)}" width="96" height="96">` : ''}</a><div class="ml-line__info"></div>`;
      }
      el.dataset.variant = String(i.variant_id);
      used.add(el);
      const html = this.#lineInfo(i);
      if (el._html !== html) {
        const info = el.querySelector('.ml-line__info');
        const active = document.activeElement;
        const typing = active && info.contains(active) && active.tagName === 'INPUT';
        if (!typing) { info.innerHTML = html; el._html = html; }
      }
      wrap.appendChild(el);
    });

    [...wrap.children].forEach((el) => { if (!used.has(el)) el.remove(); });
  }
}

if (!customElements.get('ml-cart')) customElements.define('ml-cart', MlCart);
